import React, { useEffect, useState } from 'react';
import { format, startOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import axios from 'axios';
import { Award, BarChart3, Building2, Medal, RefreshCw, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { Avatar, Pill, RANGOS, duracion } from './controlUi';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Oro, plata y bronce para los tres primeros.
const PODIO = [
    { icon: Trophy, color: 'text-amber-500', borde: 'border-amber-400/60', fondo: 'bg-amber-500/10', texto: '1.º lugar' },
    { icon: Medal, color: 'text-slate-400', borde: 'border-slate-400/60', fondo: 'bg-slate-400/10', texto: '2.º lugar' },
    { icon: Award, color: 'text-orange-600', borde: 'border-orange-500/50', fondo: 'bg-orange-500/10', texto: '3.º lugar' },
];

const nombre = (c) => `${c.employee.nombre} ${c.employee.apellido}`.trim();
const pct = (v) => (v == null ? '—' : `${String(v).replace('.', ',')} %`);

const detalle = (c) =>
    `${c.onTimeDays} de ${c.arrivals} llegadas a tiempo${c.lateDays ? ` · ${c.lateDays} tarde (${duracion(c.lateMinutes)})` : ''} · asistencia ${pct(c.attendanceRate)}`;

const TarjetaPodio = ({ c, onVer }) => {
    const p = PODIO[c.rank - 1];
    const Icono = p.icon;
    return (
        <button
            type="button"
            onClick={onVer}
            className={cn(
                'flex flex-col items-center gap-2 rounded-xl border-2 p-4 text-center transition hover:shadow-md',
                p.borde,
                p.fondo,
                c.rank === 1 && 'md:-translate-y-2',
            )}
        >
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Icono className={cn('h-5 w-5', p.color)} /> {p.texto}
            </div>
            <Avatar emp={c.employee} size="h-14 w-14" />
            <div>
                <p className="font-semibold leading-tight">{nombre(c)}</p>
                <p className="text-xs text-muted-foreground">{c.planta.nombre}</p>
            </div>
            <p className="text-3xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400">{pct(c.punctualityRate)}</p>
            <p className="text-xs text-muted-foreground">{detalle(c)}</p>
        </button>
    );
};

const FilaRanking = ({ c, onVer }) => (
    <button
        type="button"
        onClick={onVer}
        className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left transition hover:bg-muted/60"
        title={detalle(c)}
    >
        <span
            className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                c.rank <= 3 ? cn(PODIO[c.rank - 1].fondo, PODIO[c.rank - 1].color) : 'bg-muted text-muted-foreground',
            )}
        >
            {c.rank}
        </span>
        <Avatar emp={c.employee} size="h-7 w-7" />
        <div className="min-w-0 flex-1">
            <p className="text-sm font-medium leading-tight">{nombre(c)}</p>
            <p className="text-xs text-muted-foreground">
                {c.onTimeDays}/{c.arrivals} a tiempo
                {c.lateDays ? ` · ${duracion(c.lateMinutes)} tarde` : ''}
            </p>
        </div>
        <span className="text-sm font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">{pct(c.punctualityRate)}</span>
        {c.lateDays === 0 && <Pill tono="verde" className="hidden sm:inline-flex">Perfecto</Pill>}
    </button>
);

const Destacados = ({ onVerPersona }) => {
    const { toast } = useToast();
    const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [resultado, setResultado] = useState(null);
    const [loading, setLoading] = useState(false);

    const generar = async (desde = startDate, hasta = endDate) => {
        if (hasta < desde) {
            toast({ title: 'Rango no válido', description: 'La fecha fin es anterior a la de inicio.', variant: 'destructive' });
            return;
        }
        try {
            setLoading(true);
            const { data } = await axios.get(`${API_URL}/shifts/control/highlights`, { params: { startDate: desde, endDate: hasta } });
            setResultado(data);
        } catch (error) {
            console.error('Error cargando destacados:', error);
            toast({
                title: 'Error',
                description: error?.response?.data?.message || 'No se pudieron calcular los empleados destacados.',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    // Se calcula al abrir la pestaña con el mes en curso.
    useEffect(() => {
        generar();
    }, []);

    const ver = (c) => onVerPersona(c.employee.id, resultado.startDate, resultado.endDate);
    const r = resultado;

    return (
        <div className="space-y-4">
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs">Desde</Label>
                            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-44" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Hasta</Label>
                            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-44" />
                        </div>
                        <Button onClick={() => generar()} disabled={loading}>
                            {loading ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <BarChart3 className="mr-2 h-4 w-4" />}
                            {loading ? 'Calculando…' : 'Actualizar'}
                        </Button>
                        <div className="flex flex-wrap gap-2">
                            {RANGOS.map((rg) => (
                                <Button
                                    key={rg.label}
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() => {
                                        const [a, b] = rg.calc();
                                        const desde = format(a, 'yyyy-MM-dd');
                                        const hasta = format(b, 'yyyy-MM-dd');
                                        setStartDate(desde);
                                        setEndDate(hasta);
                                        generar(desde, hasta);
                                    }}
                                >
                                    {rg.label}
                                </Button>
                            ))}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {!r ? (
                <Card>
                    <CardContent className="flex h-48 items-center justify-center text-muted-foreground">
                        {loading ? 'Calculando los empleados destacados…' : 'Sin datos.'}
                    </CardContent>
                </Card>
            ) : (
                <>
                    <div className="flex flex-wrap items-end justify-between gap-2">
                        <div>
                            <h3 className="text-lg font-semibold">Los más puntuales de la empresa</h3>
                            <p className="text-xs text-muted-foreground">
                                Del {format(new Date(`${r.startDate}T12:00:00`), "d 'de' MMM yyyy", { locale: es })} al{' '}
                                {format(new Date(`${r.endDate}T12:00:00`), "d 'de' MMM yyyy", { locale: es })} · {r.summary.eligible} de{' '}
                                {r.summary.people} personas cumplen los criterios · {r.summary.perfect} sin ninguna llegada tarde ·
                                puntualidad general {pct(r.summary.punctualityRate)}
                            </p>
                        </div>
                    </div>

                    {r.company.length === 0 ? (
                        <Card>
                            <CardContent className="flex h-32 items-center justify-center text-muted-foreground">
                                Nadie cumple los criterios en este periodo.
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
                            <div className="grid grid-cols-1 items-end gap-3 pt-2 sm:grid-cols-3">
                                {/* Orden visual del podio: 2.º, 1.º, 3.º */}
                                {[1, 0, 2]
                                    .map((i) => r.company[i])
                                    .filter(Boolean)
                                    .map((c) => (
                                        <TarjetaPodio key={c.employee.id} c={c} onVer={() => ver(c)} />
                                    ))}
                            </div>
                            <Card>
                                <CardHeader className="pb-1">
                                    <CardTitle className="text-base">Top {r.company.length}</CardTitle>
                                    <CardDescription>Haz clic en una persona para ver su detalle.</CardDescription>
                                </CardHeader>
                                <CardContent className="max-h-[360px] space-y-0.5 overflow-y-auto pt-0">
                                    {r.company.slice(3).map((c) => (
                                        <FilaRanking key={c.employee.id} c={c} onVer={() => ver(c)} />
                                    ))}
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    <div>
                        <h3 className="text-lg font-semibold">Destacados por planta</h3>
                        <p className="text-xs text-muted-foreground">Las plantas van ordenadas por puntualidad general de su personal.</p>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {r.byCenter.map((p) => (
                            <Card key={p.id}>
                                <CardHeader className="pb-2">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <CardTitle className="flex items-center gap-2 truncate text-base">
                                                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" /> {p.nombre}
                                            </CardTitle>
                                            <CardDescription>
                                                {p.people} personas · {p.eligible} destacadas
                                                {p.lateMinutes ? ` · ${duracion(p.lateMinutes)} tarde en total` : ''}
                                            </CardDescription>
                                        </div>
                                        <div className="text-right">
                                            <p
                                                className={cn(
                                                    'text-lg font-semibold tabular-nums',
                                                    p.punctualityRate >= 85
                                                        ? 'text-emerald-600 dark:text-emerald-400'
                                                        : p.punctualityRate >= 60
                                                          ? 'text-amber-600 dark:text-amber-400'
                                                          : 'text-red-600 dark:text-red-400',
                                                )}
                                            >
                                                {pct(p.punctualityRate)}
                                            </p>
                                            <p className="text-[11px] text-muted-foreground">puntualidad</p>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-0.5 pt-0">
                                    {p.top.length ? (
                                        p.top.map((c) => <FilaRanking key={c.employee.id} c={c} onVer={() => ver(c)} />)
                                    ) : (
                                        <p className="py-3 text-center text-sm text-muted-foreground">Nadie cumple los criterios en el periodo.</p>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    <p className="text-xs text-muted-foreground">
                        Criterios: al menos {r.criteria.minArrivals} llegadas medidas (turno planificado y marcación) en el periodo,
                        asistencia de {r.criteria.minAttendance} % o más y {r.criteria.minPunctuality} % o más de llegadas a tiempo.
                        Se ordena por % de llegadas a tiempo, luego asistencia y luego menos minutos tarde. Llegar después de la hora
                        de inicio del turno cuenta como tarde. La planta es la de la ficha del empleado; las incapacidades y permisos
                        no cuentan como inasistencia.
                    </p>
                </>
            )}
        </div>
    );
};

export default Destacados;
